import {
  IUberEatsDenyReason,
  IUberEatsIncomingOrder,
  IUberEatsOrderItem,
  UberEatsOrderAction,
} from '../models/UberEats/UberEatsModels'

/**
 * Boundary between the incoming-order queue and the backend. The real implementation will call
 * homi-api's accept/deny endpoints (which proxy Uber's /accept_pos_order and /deny_pos_order).
 */
export interface IUberEatsOrderService {
  acceptOrder(orderId: string): Promise<void>
  denyOrder(orderId: string, reason: IUberEatsDenyReason): Promise<void>
}

const MOCK_CUSTOMERS = [
  'Sam Gamgee',
  'Frodo Baggins',
  'Rosie Cotton',
  'Merry Brandybuck',
  'Pippin Took',
]

const MOCK_ITEMS: Omit<IUberEatsOrderItem, 'quantity'>[] = [
  {
    id: 'blue-dream',
    productId: 'mock-product-blue-dream',
    name: 'Blue Dream 3.5g',
    sku: 'SKU00892',
    price: 32,
  },
  {
    id: 'rolling-papers',
    productId: 'mock-product-rolling-papers',
    name: 'Rolling Papers',
    sku: 'SKU00411',
    price: 4,
  },
  {
    id: 'gummies',
    productId: 'mock-product-gummies',
    name: 'Watermelon Gummies 10pk',
    sku: 'SKU00733',
    price: 5.2,
  },
  {
    id: 'og-kush',
    productId: 'mock-product-og-kush',
    name: 'OG Kush Pre-Roll',
    sku: 'SKU00745',
    price: 9,
  },
  {
    id: 'sour-diesel',
    productId: 'mock-product-sour-diesel',
    name: 'Sour Diesel 1g',
    sku: 'SKU00812',
    price: 19.5,
  },
]

let mockOrderSeq = 0

export const createMockIncomingOrder = (
  receivedAt = Date.now(),
  catalog: Omit<IUberEatsOrderItem, 'quantity'>[] = MOCK_ITEMS,
): IUberEatsIncomingOrder => {
  mockOrderSeq++

  const source = catalog.length ? catalog : MOCK_ITEMS
  const itemCount = (mockOrderSeq % 3) + 1
  const items = Array.from({ length: itemCount }, (_, i) => ({
    ...source[(mockOrderSeq + i) % source.length],
    quantity: ((mockOrderSeq + i) % 3) + 1,
  }))
  const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0)

  return {
    id: `mock-uber-${receivedAt}-${mockOrderSeq}`,
    displayId: String(7000 + mockOrderSeq),
    customerName: MOCK_CUSTOMERS[mockOrderSeq % MOCK_CUSTOMERS.length],
    items,
    total: Math.round(total * 100) / 100,
    receivedAt,
  }
}

/** Development and QA stand-in with controls for failure, latency and call counting. */
export class MockUberEatsOrderService implements IUberEatsOrderService {
  readonly calls: Record<UberEatsOrderAction, number> = { accept: 0, deny: 0 }

  private failures = new Set<UberEatsOrderAction>()

  private delays: Partial<Record<UberEatsOrderAction, number>> = {}

  constructor(private defaultDelayMs = 800) {}

  failNext = (action: UberEatsOrderAction) => {
    this.failures.add(action)
  }

  delayNext = (action: UberEatsOrderAction, ms: number) => {
    this.delays[action] = ms
  }

  acceptOrder = async (orderId: string) => {
    await this.handle('accept', `[Mock] Uber rejected accept for order ${orderId}`)
  }

  denyOrder: IUberEatsOrderService['denyOrder'] = async (orderId) => {
    await this.handle('deny', `[Mock] Uber rejected deny for order ${orderId}`)
  }

  private handle = async (action: UberEatsOrderAction, failureMessage: string) => {
    this.calls[action]++
    await this.simulateLatency(action)

    if (this.failures.delete(action)) throw new Error(failureMessage)
  }

  private simulateLatency(action: UberEatsOrderAction) {
    const delayMs = this.delays[action] ?? this.defaultDelayMs
    delete this.delays[action]

    if (!delayMs) return Promise.resolve()
    return new Promise<void>((resolve) => setTimeout(resolve, delayMs))
  }
}

/** Fails closed: nothing may report success without a real backend confirmation. */
export class UnavailableUberEatsOrderService implements IUberEatsOrderService {
  acceptOrder = () => Promise.reject(new Error('Uber Eats is not available yet'))

  denyOrder = () => Promise.reject(new Error('Uber Eats is not available yet'))
}

// REACT_APP_SENTRY_ENV is the per-environment value web/env already provides (FB builds get 'staging');
// allow-listing non-production values keeps production, and any unknown value, fail-closed.
const UBER_EATS_MOCK_ENVIRONMENTS = ['staging', 'sandbox']

export const isUberEatsMockEnabled = () =>
  process.env.NODE_ENV !== 'production' ||
  UBER_EATS_MOCK_ENVIRONMENTS.includes(process.env.REACT_APP_SENTRY_ENV ?? '')

// TODO: return the real homi-api backed service once the accept/deny endpoints exist.
export const createUberEatsOrderService = (): IUberEatsOrderService =>
  isUberEatsMockEnabled() ? new MockUberEatsOrderService() : new UnavailableUberEatsOrderService()

export interface IUberEatsMockControls {
  /** Queues a mock order that arrived now unless overridden */
  receive: (overrides?: Partial<IUberEatsIncomingOrder>) => IUberEatsIncomingOrder
  clear: () => void
  failNext: (action: UberEatsOrderAction) => void
  delayNext: (action: UberEatsOrderAction, ms: number) => void
  readonly calls: Record<UberEatsOrderAction, number>
}

declare global {
  interface Window {
    uberEatsMock?: IUberEatsMockControls
  }
}

interface IUberEatsMockTarget {
  receiveOrder: (payload: unknown) => void
  clearIncomingOrders: () => void
}

/**
 * Exposes `window.uberEatsMock` for QA and e2e while the backend feed does not exist.
 * Does nothing unless the mock gate is on. Returns the function that removes the controls.
 */
export const registerUberEatsMock = (
  store: IUberEatsMockTarget,
  service: IUberEatsOrderService,
): (() => void) => {
  if (!isUberEatsMockEnabled() || !(service instanceof MockUberEatsOrderService)) {
    return () => undefined
  }

  const controls: IUberEatsMockControls = {
    receive: (overrides = {}) => {
      const order = { ...createMockIncomingOrder(overrides.receivedAt ?? Date.now()), ...overrides }
      store.receiveOrder(order)
      return order
    },
    clear: store.clearIncomingOrders,
    failNext: service.failNext,
    delayNext: service.delayNext,
    get calls() {
      return { ...service.calls }
    },
  }
  window.uberEatsMock = controls

  return () => {
    if (window.uberEatsMock === controls) delete window.uberEatsMock
  }
}
